import * as React from 'react';
import { useRecoilValue } from 'recoil';
import {
  IRide,
  IRidePosition,
  TRideActiveLayer,
  IRideEventLinePaths,
  IRideMapEventLines,
} from '../types';
import { IS_EVENT_LAYER } from '../constants';
import { activeRideNumberAtom, selectedRidesAtom } from '../RidePlayerState';
import { createEventLines, updateEventLines } from '../RidePlayerService';

interface IUseEventLine {
  eventLines: IRideMapEventLines;
  setEventLinesSize: (strokeWeight: number) => void;
  showAllEventLines: () => void;
  hideAllEventLines: () => void;
  setEventLinesOpacity: (rideNumber: number, opacity: number) => void;
  setAllEventLinesColor: (color: string, exceptRideNumber?: number) => void;
  setEventLinesColorForRide: (rideNumber: number, color: string) => void;
  resetAllEventLinesColor: () => void;
  setEventLineZIndex: (rideNumber: number, zIndex: number) => void;
  setAllEventLinesZIndex: (zIndex: number, exceptRideNumber?: number) => void;
  removeAllEventLines: () => void;
}

/**
 * useEventLine custom hook
 * @param {google.maps.Map} map
 * @returns {IUseEventLine}
 */
const useEventLine = (
  map: google.maps.Map,
  rides: IRide[],
  activeLayer: TRideActiveLayer,
  actualRidePosition?: IRidePosition | null
): IUseEventLine => {
  const eventLines = React.useRef<IRideMapEventLines>({});
  const eventPaths = React.useRef<IRideEventLinePaths>({});
  const selectedRides = useRecoilValue<number[]>(selectedRidesAtom);
  const activeRide = useRecoilValue<number>(activeRideNumberAtom);

  /**
   * Set all event lines color
   * @param {string} color
   * @param {number} exceptRideNumber
   * @returns {void}
   */
  const setAllEventLinesColor = (color: string, exceptRideNumber?: number): void => {
    if (eventLines.current) {
      Object.keys(eventLines.current).forEach((rideNumber: string) => {
        if (exceptRideNumber && exceptRideNumber === Number(rideNumber)) {
          return;
        }
        const polylines: google.maps.Polyline[] = eventLines.current[rideNumber];
        polylines.forEach((polyline: google.maps.Polyline) => {
          polyline.setOptions({ strokeColor: color });
        });
      });
    }
  };

  /**
   * Set event lines color for specified ride
   * @param {number} rideNumber
   * @param {string} color
   * @returns {void}
   */
  const setEventLinesColorForRide = (rideNumber: number, color: string): void => {
    if (eventLines.current[rideNumber]) {
      const polylines: google.maps.Polyline[] = eventLines.current[rideNumber];
      polylines.forEach((polyline: google.maps.Polyline) => {
        polyline.setOptions({ strokeColor: color });
      });
    }
  };

  /**
   * Reset event lines color for all rides
   * @param {number} rideNumber
   * @param {string} color
   * @returns {void}
   */
  const resetAllEventLinesColor = (): void => {
    Object.keys(eventLines.current).forEach((rideNumber: string) => {
      const polylines: google.maps.Polyline[] = eventLines.current[rideNumber];
      polylines.forEach((polyline: google.maps.Polyline) => {
        const originalColor = polyline.get('originalColor');
        polyline.setOptions({ strokeColor: originalColor });
      });
    });
  };

  /**
   * Set event lines stroke weight (aka size)
   * @param {number} strokeWeight
   * @returns {void}
   */
  const setEventLinesSize = (strokeWeight = 2): void => {
    Object.keys(eventLines.current).forEach((rideNumber: string) => {
      const polylines = eventLines[rideNumber];
      polylines.forEach((polyline: any) => {
        if (polyline.strokeWeight !== strokeWeight) {
          polyline.setOptions({ strokeWeight });
        }
      });
    });
  };

  /**
   * Show all event lines
   * @returns {void}
   */
  const showAllEventLines = (): void => {
    Object.keys(eventLines.current).forEach((rideNumber: string) => {
      eventLines.current[rideNumber].forEach((polyline: google.maps.Polyline) => {
        if (!polyline.getMap() && selectedRides.includes(Number(rideNumber))) {
          polyline.setMap(map);
        }
      });
    });
  };

  /**
   * Hide all event lines
   * @returns {void}
   */
  const hideAllEventLines = (): void => {
    Object.keys(eventLines.current).forEach((rideNumber: string) => {
      eventLines.current[rideNumber].forEach((polyline: google.maps.Polyline) => {
        if (polyline.getMap()) {
          polyline.setMap(null);
        }
      });
    });
  };

  /**
   * Set event line opacity for specified ride
   * @param {number} rideNumber
   * @param {number} opacity
   */
  const setEventLinesOpacity = (rideNumber: number, opacity: number): void => {
    if (eventLines.current && eventLines.current[rideNumber]) {
      const polylines = eventLines.current[String(rideNumber)];
      polylines.forEach((polyline: any) => {
        if (polyline && polyline.strokeOpacity) {
          polyline.setOptions({ strokeOpacity: opacity });
        }
      });
    }
  };

  const removeAllEventLines = () => {
    hideAllEventLines();
    eventLines.current = {};
  };

  /**
   * Set event line zindex for specified ride
   * @param {number} rideNumber
   * @param {number} zIndex
   * @returns {void}
   */
  const setEventLineZIndex = (rideNumber: number, zIndex: number): void => {
    if (eventLines.current && eventLines.current[rideNumber]) {
      const polylines = eventLines.current[String(rideNumber)];
      polylines.forEach((polyline: any) => {
        if (polyline && polyline.strokeOpacity) {
          polyline.setOptions({ zIndex });
        }
      });
    }
  };

  /**
   * Set all event lines zIndex
   * @param {number} zIndex
   * @param {number} exceptRideNumber
   * @returns {void}
   */
  const setAllEventLinesZIndex = (zIndex: number, exceptRideNumber?: number): void => {
    if (eventLines.current) {
      Object.keys(eventLines.current).forEach((rideNumber: string) => {
        if (exceptRideNumber && exceptRideNumber === Number(rideNumber)) {
          return;
        }
        const polylines: google.maps.Polyline[] = eventLines.current[rideNumber];
        polylines.forEach((polyline: google.maps.Polyline) => {
          polyline.setOptions({ zIndex });
        });
      });
    }
  };

  // Handle show/hide event lines by selected rides
  React.useEffect(() => {
    Object.keys(eventLines.current).forEach((rideNumber: string) => {
      eventLines.current[Number(rideNumber)].forEach((polyline: google.maps.Polyline) => {
        if (!selectedRides.includes(Number(rideNumber)) && polyline.getMap()) {
          polyline.setMap(null);
        }
        if (
          selectedRides.includes(Number(rideNumber)) &&
          !polyline.getMap() &&
          activeLayer === IS_EVENT_LAYER
        ) {
          polyline.setMap(map);
        }
      });
    });
  }, [activeLayer, map, selectedRides]);

  // Cretae event lines
  React.useEffect(() => {
    let showOnMap = false;
    if (activeLayer === IS_EVENT_LAYER) {
      showOnMap = true;
    }
    eventLines.current = createEventLines(showOnMap ? map : null, rides, eventPaths.current);
    // Unset event lines from map
    return () => {
      hideAllEventLines();
      eventPaths.current = {};
      eventLines.current = {};
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If actual position is present, update event lines
  React.useEffect(() => {
    if (actualRidePosition && activeRide) {
      updateEventLines(
        activeLayer === IS_EVENT_LAYER ? map : undefined,
        rides,
        eventLines.current,
        eventPaths.current,
        activeRide,
        actualRidePosition
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actualRidePosition, activeRide]);

  const data: IUseEventLine = {
    eventLines: eventLines.current,
    setEventLinesSize,
    showAllEventLines,
    hideAllEventLines,
    setEventLinesOpacity,
    setAllEventLinesColor,
    setEventLinesColorForRide,
    resetAllEventLinesColor,
    setEventLineZIndex,
    setAllEventLinesZIndex,
    removeAllEventLines,
  };

  return data;
};

export default useEventLine;
